import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';

import { AddOnCancelFailure } from 'store/api-client/add-on-cancel-types';
import { TransactionDetails } from 'store/transactions/types';
import { addOnOrderActions } from 'components/ordered-add-ons/add-on-actions';
import { configSelector } from 'store/config/selectors';
import { markAddOnsCancelled } from 'store/booking-add-ons/actions';
import { useCancelAddOnOrderMutation } from 'data/mutations/useCancelAddOnOrderMutation.hook';
import { useOrderedAddOnPaymentQuery } from 'data/queries/useOrderedAddOnPaymentQuery.hook';

export interface RemoveAllAddOns {
  /** Draw the control at all. False on a first-time order, and on one the service will not stand down. */
  canRemoveAll: boolean;
  onRemoveAll: VoidFunction;
  isRemoving: boolean;
  /** The refusal to say, in the tenant's words. Null while nothing has been refused. */
  removeFailureMessageId: string | null;
}

/*
 * The control is drawn from the service's verdict, not from the picker being open: `addOnOrderActions`
 * emits `amend` with no `cancel` for a paid order a vendor has already started, so offering it anyway
 * buys a 409. Shares the card's query key, so an invalidation from either is seen by both.
 */
export const useRemoveAllAddOns = (booking: TransactionDetails | undefined, onDone: VoidFunction): RemoveAllAddOns => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { buildingUuid } = useSelector(configSelector);

  /* `details.id`, not the transaction's own `id`; helix types it `string | number`. */
  const rawBookingId = Number(booking?.details?.id);
  const bookingId = Number.isFinite(rawBookingId) ? rawBookingId : null;
  /* Gates the read: no vendor rows means nothing to remove, so there is no verdict worth fetching. */
  const hasOrder = Boolean(booking?.details?.vendor_add_ons?.length);

  const { data: orderedRead } = useOrderedAddOnPaymentQuery(buildingUuid ?? null, bookingId, hasOrder);

  const { actions } = addOnOrderActions({
    payment: orderedRead?.payment ?? null,
    canOrderAddOns: Boolean(orderedRead?.canOrderAddOns),
    amendRefusedReason: orderedRead?.amendRefusedReason ?? null,
  });

  const { mutateAsync: cancelAddOnOrder, isPending: isRemoving } = useCancelAddOnOrderMutation();
  const [failure, setFailure] = useState<AddOnCancelFailure | null>(null);

  /*
   * Reads the resolved value, never `isError`: the mutation does not reject, so `isSuccess` is true on
   * a refusal too. Three of its four outcomes are failures, and the message id carries which.
   */
  const onRemoveAll = useCallback(async () => {
    if (!buildingUuid || bookingId === null) {
      return;
    }

    setFailure(null);

    const outcome = await cancelAddOnOrder({ buildingUuid, bookingId });

    if (outcome.kind !== 'cancelled') {
      setFailure(outcome.kind);

      return;
    }

    /*
     * Dispatched before the close, and the order matters: closing remounts the booking-details card,
     * which reads this mark on its first render to decide whether to keep asking after the refund.
     */
    dispatch(markAddOnsCancelled({ bookingId: String(bookingId), at: Date.now() }));
    queryClient.invalidateQueries({ queryKey: ['ordered-add-on-payment', buildingUuid ?? null, bookingId] });
    onDone();
  }, [cancelAddOnOrder, dispatch, queryClient, buildingUuid, bookingId, onDone]);

  return {
    canRemoveAll: hasOrder && actions.includes('cancel') && Boolean(buildingUuid) && bookingId !== null,
    onRemoveAll,
    isRemoving,
    removeFailureMessageId: failure && `add_on_cancel_${failure}`,
  };
};
